import React from 'react';
import { Box, Typography } from '@mui/material';
import { useThemeStore } from '@diana/core';
import { Marker } from '../Icons/Marker';
import { useTranslation } from 'react-i18next';
import { HORIZONTAL_SCALE_NS } from './i18n';

interface Range {
  min: number;
  max: number;
}

interface RangeData {
  ideal: Range;
  warning: Range;
}

interface HorizontalScaleProps {
  min: number;
  max: number;
  step: number;
  average: number | null;
  instant: number | null;
  unit?: string;
  range?: RangeData;
  showInstant?: boolean;
  cardHeight?: string;
}

export const HorizontalScale: React.FC<HorizontalScaleProps> = ({
  min,
  max,
  step,
  average,
  instant,
  unit,
  range,
  showInstant = true,
}) => {
  const { t } = useTranslation([HORIZONTAL_SCALE_NS]);
  const values: number[] = [];
  for (let i = min; i <= max; i += step) {
    values.push(i);
  }

  const themeName = useThemeStore((store) => store.themeName);
  const textColor = '#65686B';
  const valueColor = themeName === 'dianaLight' ? '#0C0C0C' : '#fff';

  const totalValues = max - min;
  const averagePosition = average
    ? Math.min(Math.max(((average - min) / totalValues) * 100, 0), 100)
    : 0;

  const instantPosition = instant
    ? Math.min(Math.max(((instant - min) / totalValues) * 100, 0), 100)
    : 0;

  // Calculate color zones based on range data
  const getColorZones = () => {
    if (!range) {
      return [
        { width: 10, color: '#F2D9C2' },
        { width: 80, color: '#26BF63' },
        { width: 10, color: '#F2D9C2' },
      ];
    }

    const zones = [];
    const totalRange = max - min;

    // Left warning zone
    if (range.warning.min < range.ideal.min) {
      const leftWarningWidth = ((range.ideal.min - range.warning.min) / totalRange) * 100;
      zones.push({ width: leftWarningWidth, color: '#F2D9C2' });
    }

    // Ideal zone (green)
    const idealWidth = ((range.ideal.max - range.ideal.min) / totalRange) * 100;
    zones.push({ width: idealWidth, color: '#26BF63' });

    // Right warning zone
    if (range.warning.max > range.ideal.max) {
      const rightWarningWidth = ((range.warning.max - range.ideal.max) / totalRange) * 100;
      zones.push({ width: rightWarningWidth, color: '#F2D9C2' });
    }

    const totalZoneWidth = zones.reduce((sum, zone) => sum + zone.width, 0);
    if (totalZoneWidth < 100) {
      const remainingWidth = 100 - totalZoneWidth;
      if (zones.length === 0) {
        zones.push({ width: remainingWidth, color: '#D7DCDE' });
      } else {
        // Add gray zones at the beginning and end if needed
        if (range.warning.min > min) {
          const leftGrayWidth = ((range.warning.min - min) / totalRange) * 100;
          zones.unshift({ width: leftGrayWidth, color: '#D7DCDE' });
        }
        if (range.warning.max < max) {
          const rightGrayWidth = ((max - range.warning.max) / totalRange) * 100;
          zones.push({ width: rightGrayWidth, color: '#D7DCDE' });
        }
      }
    }

    return zones;
  };

  const colorZones = getColorZones();

  const instantValue =
    instant !== null
      ? `${instant.toFixed(2)} ${unit}`
      : t('components/HorizontalScale:emptyValuePlaceholder');
  const instantText =
    instant !== null ? t('components/HorizontalScale:instant') : t('components/HorizontalScale:na');
  const avegageValue =
    average !== null
      ? `${average.toFixed(2)} ${unit}`
      : t('components/HorizontalScale:emptyValuePlaceholder');
  const averageText =
    average !== null ? t('components/HorizontalScale:average') : t('components/HorizontalScale:na');

  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent={instant !== null ? 'space-between' : 'space-around'}
      width="100%"
      sx={{ mr: '10px' }}
    >
      {showInstant && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="body1"
            sx={{
              color: valueColor,
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              textAlign: 'center',
            }}
          >
            {instantValue}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: textColor, fontWeight: 400, fontSize: '12px', lineHeight: '16px' }}
          >
            {instantText}
          </Typography>
        </Box>
      )}
      <Box
        display="flex"
        flex="1"
        flexDirection="column"
        sx={{ maxWidth: '180px', position: 'relative', minWidth: '120px', mx: '15px' }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          position="relative"
          sx={{ mb: '10px' }}
        >
          <Box sx={{ position: 'relative', flexGrow: 1 }}>
            <Box
              sx={{
                width: '100%',
                height: '6px',
                position: 'relative',
                display: 'flex',
                backgroundColor: '#D7DCDE',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              {colorZones.map((zone, index) => (
                <Box
                  key={index}
                  sx={{
                    width: `${zone.width}%`,
                    height: '100%',
                    backgroundColor: zone.color,
                  }}
                />
              ))}
            </Box>
            {!showInstant && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '-13.5px',
                  left: `${averagePosition}%`,
                  transform: 'translateX(-50%)',
                  zIndex: 3,
                }}
              >
                <Marker bgColor={valueColor} />
              </Box>
            )}
            {showInstant && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '-13.5px',
                  left: `${instantPosition}%`,
                  transform: 'translateX(-50%)',
                  zIndex: 3,
                }}
              >
                <Marker bgColor={valueColor} />
              </Box>
            )}
          </Box>
        </Box>
        <Box>
          {values.map((value, index) => {
            const valuePosition = ((value - min) / totalValues) * 100;
            //TODO: Uncomment this block to render minor ticks
            // const isLastValue = index === values.length - 1;

            return (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  left: `${valuePosition}%`,
                  transform: 'translateX(-50%)',
                }}
              >
                {/* Render major ticks */}
                <Box
                  sx={{
                    width: '1px',
                    height: '8px', // Major ticks are longer
                    backgroundColor: '#D7DCDE',
                    marginBottom: '4px',
                    ml: '14px',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '12px',
                    textAlign: 'center',
                    marginTop: '4px',
                    whiteSpace: 'nowrap',
                    minWidth: '30px',
                  }}
                >
                  {value}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', ml: '10px' }}>
        <Typography
          variant="body1"
          sx={{
            color: valueColor,
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            textAlign: 'center',
          }}
        >
          {avegageValue}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: textColor, fontWeight: 400, fontSize: '12px', lineHeight: '16px' }}
        >
          {averageText}
        </Typography>
      </Box>
    </Box>
  );
};
